import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtPrice, conditionLabel } from "@/lib/market";
import {
  ArrowLeft, MapPin, Tag, BadgeCheck, Flame, CreditCard, Loader2,
  Truck, X, Edit3, Trash2, CheckCircle2, RotateCcw, ShieldCheck,
} from "lucide-react";

export default function MarketView() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [i, setI] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [err, setErr] = useState("");
  const [boostOpen, setBoostOpen] = useState(false);
  const [tiers, setTiers] = useState({});
  const [paying, setPaying] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/marketplace/${id}`); setI(data); }
    catch (e) { setErr(formatErr(e)); }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    api.get("/promotions/tiers").then(r => setTiers(r.data || {})).catch(()=>{});
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("boost") === "done" && sp.get("session_id")) {
      api.get(`/marketplace/boost/status/${sp.get("session_id")}`).finally(load);
    }
  }, []);

  const buy = async (withShipping) => {
    if (!user) { nav("/login"); return; }
    setPaying(true); setErr("");
    try {
      const { data } = await api.post(`/marketplace/${id}/checkout`,
        { origin_url: window.location.origin, shipping: !!withShipping });
      if (data.url) window.location.href = data.url;
    } catch (e) { setErr(formatErr(e)); }
    finally { setPaying(false); }
  };

  const toggleSold = async () => {
    try { await api.post(`/marketplace/${id}/mark-sold`); load(); }
    catch (e) { setErr(formatErr(e)); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this listing?")) return;
    try { await api.delete(`/marketplace/${id}`); nav("/market/mine"); }
    catch (e) { setErr(formatErr(e)); }
  };

  if (err) return <div className="max-w-3xl mx-auto p-10"><p className="text-sm text-[#B84B4B]">{err}</p></div>;
  if (!i) return <div className="max-w-3xl mx-auto p-10 text-[#8A8A8A]">Loading…</div>;

  const isSold = i.status === "sold";

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-10" data-testid="market-view">
      <Link to="/market" className="text-sm text-[#4A4A4A] inline-flex items-center gap-1 mb-5 hover:text-[#0A0A0A]"><ArrowLeft size={14}/> Back to listings</Link>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
        <div>
          <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-[#F2F2F0]">
            {i.images?.[imgIdx] ? (
              <img src={i.images[imgIdx]} alt="" className="w-full h-full object-cover"/>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#8A8A8A]"><Tag size={40}/></div>
            )}
          </div>
          {i.images?.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3" data-testid="market-image-thumbs">
              {i.images.map((src, k) => (
                <button key={k} onClick={()=>setImgIdx(k)}
                  className={`aspect-square rounded-xl overflow-hidden bg-[#F2F2F0] border-2 ${k===imgIdx?"border-[#0A0A0A]":"border-transparent"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover"/>
                </button>
              ))}
            </div>
          )}
        </div>

        <aside>
          <p className="overline">{i.category} · {conditionLabel(i.condition)}</p>
          <div className="flex items-center flex-wrap gap-2 mt-1">
            <h1 className="h-display text-3xl flex-1" data-testid="market-title">{i.title}</h1>
            {i.is_boosted && (
              <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#0057FF] text-white inline-flex items-center gap-1">
                <Flame size={10}/> {i.boost_tier}
              </span>
            )}
            {isSold && (
              <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#0A0A0A] text-white">Sold</span>
            )}
          </div>
          <p className="text-3xl font-medium mt-3" data-testid="market-price">{fmtPrice(i)}</p>
          <p className="text-sm text-[#4A4A4A] mt-1 inline-flex items-center gap-1">
            <MapPin size={12}/> {i.location || "—"}
          </p>

          <div className="card p-5 mt-5">
            <Link to={`/u/${i.seller_id}`} className="flex items-center gap-3 hover:opacity-80">
              <img src={i.seller?.avatar} alt="" className="h-10 w-10 rounded-full object-cover bg-[#E5E5E3]"/>
              <div className="flex-1 min-w-0">
                <p className="font-medium flex items-center gap-1">{i.seller?.name}
                  {i.seller?.verified && <BadgeCheck size={13} className="text-[#0A0A0A]"/>}
                </p>
                <p className="text-xs text-[#8A8A8A]">{i.seller?.location}</p>
              </div>
            </Link>
          </div>

          {i.is_owner ? (
            <div className="card p-4 mt-3 flex flex-wrap gap-2" data-testid="market-owner-controls">
              <p className="text-sm text-[#4A4A4A] flex-1">Your listing · status <span className="font-medium text-[#0A0A0A]">{i.status}</span></p>
              <button onClick={toggleSold} className="btn-ghost text-sm" data-testid="market-toggle-sold">
                {isSold ? <><RotateCcw size={13}/> Relist</> : <><CheckCircle2 size={13}/> Mark sold</>}
              </button>
              <button onClick={()=>setBoostOpen(true)} className="btn-secondary !py-2 !px-3 text-sm" data-testid="market-boost-open">
                <Flame size={13}/> Boost
              </button>
              <Link to={`/market/new?edit=${i.id}`} className="btn-ghost text-sm" data-testid="market-edit"><Edit3 size={13}/> Edit</Link>
              <button onClick={remove} className="btn-ghost text-sm text-[#B84B4B]" data-testid="market-delete"><Trash2 size={13}/> Delete</button>
            </div>
          ) : isSold ? (
            <div className="card p-4 mt-3 text-center" data-testid="market-sold-banner">
              <p className="text-[#4A4A4A]">This item has been sold.</p>
            </div>
          ) : (
            <div className="card p-4 mt-3 space-y-2" data-testid="market-buyer-cta">
              <button onClick={()=>buy(false)} disabled={paying} className="btn-primary w-full" data-testid="market-buy">
                {paying ? <><Loader2 size={14} className="animate-spin"/> Opening Stripe…</> : <><CreditCard size={14}/> Buy now · {fmtPrice(i)}</>}
              </button>
              {i.shipping_available && (
                <button onClick={()=>buy(true)} disabled={paying} className="btn-secondary w-full" data-testid="market-buy-shipping">
                  <Truck size={14}/> Buy + ship · {fmtPrice({ ...i, price: (i.price + (i.shipping_fee||0)) })}
                </button>
              )}
              <p className="text-[11px] text-[#8A8A8A] text-center inline-flex items-center justify-center gap-1 w-full">
                <ShieldCheck size={11}/> Paid securely through Hoomanz · 2 % Hoomanz Tip added on top
              </p>
              {err && <p className="text-sm text-[#B84B4B] text-center" data-testid="market-buy-error">{err}</p>}
            </div>
          )}
        </aside>
      </div>

      <div className="card p-6 mt-8 max-w-3xl">
        <p className="overline mb-2">Description</p>
        <p className="whitespace-pre-wrap text-[#0A0A0A]" data-testid="market-description">{i.description}</p>
      </div>

      {boostOpen && (
        <BoostModal listing={i} tiers={tiers} onClose={()=>setBoostOpen(false)}/>
      )}
    </div>
  );
}

function BoostModal({ listing, tiers, onClose }) {
  const [picking, setPicking] = useState(null);
  const [err, setErr] = useState("");
  const buy = async (tier) => {
    setPicking(tier);
    try {
      const { data } = await api.post(`/marketplace/${listing.id}/boost/checkout`, { tier, origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) { setErr(formatErr(e)); setPicking(null); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="market-boost-modal">
      <div onClick={(e)=>e.stopPropagation()} className="bg-white rounded-3xl p-7 max-w-lg w-full">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl">Boost this listing</h3>
          <button onClick={onClose} className="text-[#8A8A8A] hover:text-[#0A0A0A]"><X size={16}/></button>
        </div>
        <div className="space-y-2">
          {Object.entries(tiers).map(([id, t]) => (
            <button key={id} onClick={()=>buy(id)} disabled={!!picking}
              data-testid={`market-boost-tier-${id}`}
              className="w-full card p-4 text-left hover:border-[#0A0A0A] transition-colors">
              <div className="flex items-center gap-3">
                <Flame size={16} className="text-[#0057FF]"/>
                <div className="flex-1">
                  <p className="font-medium">{t.label}</p>
                  <p className="text-xs text-[#4A4A4A]">{t.blurb}</p>
                </div>
                <p className="font-medium">${t.price}</p>
                {picking === id && <Loader2 size={14} className="animate-spin"/>}
              </div>
            </button>
          ))}
        </div>
        {err && <p className="text-sm text-[#B84B4B] mt-3">{err}</p>}
      </div>
    </div>
  );
}
